import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

export interface PeopleSettings {
  allowMemberSelfEdit: boolean;
  allowMemberPhotoUpdate: boolean;
  updatedAt: string | null;
}

interface PeopleSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PeopleSettingsDialog = ({ open, onOpenChange }: PeopleSettingsDialogProps) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['people', 'settings'],
    queryFn: () => api.get<PeopleSettings>('/people/settings'),
    enabled: open,
  });

  const [allowMemberSelfEdit, setAllowMemberSelfEdit] = useState(false);
  const [allowMemberPhotoUpdate, setAllowMemberPhotoUpdate] = useState(false);

  useEffect(() => {
    if (!open || !settings.data) return;
    setAllowMemberSelfEdit(settings.data.allowMemberSelfEdit);
    setAllowMemberPhotoUpdate(settings.data.allowMemberPhotoUpdate);
  }, [open, settings.data]);

  const syncDraft = () => {
    if (!settings.data) return;
    setAllowMemberSelfEdit(settings.data.allowMemberSelfEdit);
    setAllowMemberPhotoUpdate(settings.data.allowMemberPhotoUpdate);
  };

  const save = useMutation({
    mutationFn: () =>
      api.patch<PeopleSettings>('/people/settings', {
        allowMemberSelfEdit,
        allowMemberPhotoUpdate: allowMemberSelfEdit ? allowMemberPhotoUpdate : false,
      }),
    onSuccess: () => {
      toast.success(t('members.settings.saved'));
      void queryClient.invalidateQueries({ queryKey: ['people', 'settings'] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const dirty =
    settings.data != null &&
    (allowMemberSelfEdit !== settings.data.allowMemberSelfEdit ||
      allowMemberPhotoUpdate !== settings.data.allowMemberPhotoUpdate);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) syncDraft();
      }}
    >
      <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{t('members.settings.title')}</DialogTitle>
          <DialogDescription>{t('members.settings.description')}</DialogDescription>
        </DialogHeader>
        <DialogBody contentClassName="flex flex-col gap-6">
          <SwitchField
            id="allow-member-self-edit"
            label={t('members.settings.allowSelfEdit.label')}
            description={t('members.settings.allowSelfEdit.description')}
            checked={allowMemberSelfEdit}
            onCheckedChange={(checked) => {
              setAllowMemberSelfEdit(checked);
              if (!checked) setAllowMemberPhotoUpdate(false);
            }}
            disabled={settings.isLoading || save.isPending}
          />

          <div className="rounded-lg border border-border p-4">
            <p className="mb-4 text-body font-medium text-fg-primary">
              {t('members.settings.photoSection.title')}
            </p>
            <SwitchField
              id="allow-member-photo-update"
              label={t('members.settings.allowPhotoUpdate.label')}
              description={t('members.settings.allowPhotoUpdate.description')}
              checked={allowMemberPhotoUpdate}
              onCheckedChange={setAllowMemberPhotoUpdate}
              disabled={!allowMemberSelfEdit || settings.isLoading || save.isPending}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={save.isPending}
            onClick={() => {
              syncDraft();
              onOpenChange(false);
            }}
          >
            {tCommon('action.cancel')}
          </Button>
          <Button
            type="button"
            loading={save.isPending}
            disabled={!dirty || settings.isLoading}
            onClick={() => save.mutate()}
          >
            {tCommon('action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
